import React from 'react';
import { motion, useReducedMotion } from 'motion/react';

type MarketingRevealProps = {
  children: React.ReactNode;
  className?: string;
  /** Stagger delay in seconds (keep small — large delays read as broken layout). */
  delay?: number;
};

/**
 * Marketing sections must never ship invisible content: `whileInView` +
 * `opacity: 0` initial left large blank regions when intersection never fired.
 * This wrapper animates on mount with a high minimum opacity, and skips motion
 * when the user prefers reduced motion.
 */
export function MarketingReveal({ children, className, delay = 0 }: MarketingRevealProps) {
  const reduceMotion = useReducedMotion();

  if (reduceMotion) {
    return <div className={className}>{children}</div>;
  }

  return (
    <motion.div
      className={className}
      initial={{ opacity: 0.94, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay, ease: 'easeOut' }}
    >
      {children}
    </motion.div>
  );
}

type MarketingHeroRevealProps = {
  children: React.ReactNode;
  className?: string;
};

/** Hero copy above the fold — short fade-in on mount only. */
export function MarketingHeroReveal({ children, className }: MarketingHeroRevealProps) {
  const reduceMotion = useReducedMotion();

  if (reduceMotion) {
    return <div className={className}>{children}</div>;
  }

  return (
    <motion.div
      className={className}
      initial={{ opacity: 0.96, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, ease: 'easeOut' }}
    >
      {children}
    </motion.div>
  );
}
